import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ReportsService } from "../../services/ReportService";

/**
 * Hook to fetch reports for an organization
 * @param {string} orgId - Organization ID
 * @returns {object} Query result with reports data
 */
export function useReports(orgId) {
  return useQuery({
    queryKey: ["reports", orgId],
    queryFn: async () => {
      if (!orgId) return [];

      const response = await ReportsService.fetchReports(orgId);
      return response || [];
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single report with full details
 * @param {string|number} reportId - Report ID
 * @returns {object} Query result with report data
 */
export function useReport(reportId) {
  return useQuery({
    queryKey: ["report", reportId],
    queryFn: async () => {
      if (!reportId) return null;

      const response = await ReportsService.fetchReport(reportId);
      return response || null;
    },
    enabled: !!reportId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new report
 * @returns {object} Mutation object
 */
export function useCreateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      schedule_id,
      schedule_time,
      schedule_day,
      devices,
      contacts,
      organisation_id,
    }) =>
      ReportsService.addReport(
        name,
        schedule_id,
        schedule_time,
        schedule_day,
        devices,
        contacts,
        organisation_id
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["reports", variables.organisation_id],
      });
    },
  });
}

/**
 * Hook to update a report
 * @returns {object} Mutation object
 */
export function useUpdateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }) =>
      ReportsService.updateReport(reportId, data),
    onSuccess: (_, { reportId, data }) => {
      // Invalidate the single report cache
      queryClient.invalidateQueries({
        queryKey: ["report", reportId],
      });
      // Invalidate the reports list cache
      queryClient.invalidateQueries({
        queryKey: ["reports", data.organisation_id],
      });
    },
  });
}

/**
 * Hook to delete a report
 * @returns {object} Mutation object
 */
export function useDeleteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId }) => ReportsService.deleteReport(reportId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["reports", orgId],
      });
    },
  });
}
